import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Paper, useTheme } from '@material-ui/core';

import dottedArrowRedirection from 'assets/icons/dottedArrowRedirection.svg';
import ErrorView from 'components/ErrorView';
import {
  challengesStatusSelector,
  historySelectedChallengeLoadingSelector,
  historySelectedChallengeSelector,
  sortedDeclaredChallengesSelector,
} from 'reducers/challenge.reducer';
import { ICustomTheme } from 'interfaces';
import Header from '../sharedComponents/Header';
import Declarations from '../sharedComponents/Declarations';
import { productCurrencySelector } from 'reducers/app.reducer';
import Skeletons from './Skeletons';
import { GOAL, LOADING_STATUS } from 'constants/index';
import WarningTextNoDeclarations from './WarningTextNoDeclarations';

const HistoryRightSection = () => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const declaredChallengesLoadingStatus = useSelector(challengesStatusSelector);
  const isChallengesOnPendingStatus =
    declaredChallengesLoadingStatus === LOADING_STATUS.PENDING;
  const isChallengesOnResolvedStatus =
    declaredChallengesLoadingStatus === LOADING_STATUS.RESOLVED;
  const declaredChallenges = useSelector(sortedDeclaredChallengesSelector);

  const historySelectedChallengeData = useSelector(
    historySelectedChallengeSelector,
  );
  const historySelectedChallengeLoadingStatus = useSelector(
    historySelectedChallengeLoadingSelector,
  );
  const isSelectedChallengeOnPendingStatus =
    historySelectedChallengeLoadingStatus === LOADING_STATUS.PENDING;

  const rewardUnit = useSelector(productCurrencySelector);
  const resultUnit =
    historySelectedChallengeData?.data?.goalType === GOAL.RESULT
      ? historySelectedChallengeData?.data?.resultUnit
      : historySelectedChallengeData?.data?.goalsUnit;
  // if isGoalMecanicByProduct is true we have to display the potential rewards to be gained
  // in other cases, we have to display the results to be gained
  const isGoalMecanicByProduct =
    !historySelectedChallengeData?.data?.isRewardingGlobal;
  const unitToDisplay = isGoalMecanicByProduct ? rewardUnit : resultUnit;

  // manage 'loading' states
  if (isChallengesOnPendingStatus || isSelectedChallengeOnPendingStatus) {
    return (
      <Paper style={{ padding: theme.spacing(2) }}>
        <Skeletons component={'Paper'} isMobile={false} />
      </Paper>
    );
  }

  // manage the 'no challenges declared' state
  // and manage 'historySelectedChallengeData failed' state
  if (!historySelectedChallengeData) {
    if (isChallengesOnResolvedStatus && declaredChallenges.length === 0) {
      return (
        <React.Fragment>
          <WarningTextNoDeclarations />
        </React.Fragment>
      );
    } else {
      return (
        <ErrorView
          errorText={t('challenges.declaration.history.failed_call_data')}
          actionText={t(
            'challenges.declaration.history.action_failed_call_data',
          )}
          desktopImage={dottedArrowRedirection}
          isMobile={false}
        />
      );
    }
  }

  // manage the 'no existing url id' state = 'not in declared challenges list' state
  // '/history/notExistingUrl'
  if (
    historySelectedChallengeData.selectedChallengeId &&
    !historySelectedChallengeData.isInDeclaredChallengesList
  ) {
    return (
      <ErrorView
        errorText={t('challenges.declaration.history.link_does_not_exist')}
        actionText={t('challenges.declaration.history.action_choice')}
        desktopImage={dottedArrowRedirection}
        isMobile={false}
      />
    );
  }

  // manage the 'is in declared challenges list' state
  // '/history' because the challengeId is set to the first element of the declared challenges
  // or '/history/existingUrl'
  if (
    historySelectedChallengeData.selectedChallengeId &&
    historySelectedChallengeData.isInDeclaredChallengesList
  ) {
    const isDeclarationAvailable =
      historySelectedChallengeData.data?.isActionOnGoing &&
      (historySelectedChallengeData.data?.isDeclarationMultiple ||
        (!historySelectedChallengeData.data?.countDeclarationsWaiting &&
          !historySelectedChallengeData.data?.countDeclarationsValidated));

    return (
      <Paper style={{ padding: theme.spacing(2) }}>
        <Header
          action={historySelectedChallengeData.data}
          isDeclarationAvailable={isDeclarationAvailable}
        />
        {historySelectedChallengeData.data?.declarations && (
          <Declarations
            declarations={historySelectedChallengeData.data?.declarations}
            unitToDisplay={unitToDisplay}
            isGoalMecanicByProduct={isGoalMecanicByProduct}
          />
        )}
      </Paper>
    );
  }

  return null;
};

export default HistoryRightSection;
